import { mapActions, mapGetters } from 'vuex';
import startCase from 'lodash/startCase';
import format from '@/common/utilities/format';
import mvtHeader from '@/common/components/mvtheader/mvt-header.vue';
import mvtPopSignin from '@/common/components/mvtpopsignin/mvt-popsignin.vue';
import mvtFooter from '@/common/components/mvtfooter/mvt-footer.vue';
import mvtDialogCenter from '@/common/components/mvtdialogcenter/mvt-dialogcenter.vue';
import mvtToastCenter from '@/common/components/mvttoastcenter/mvt-toastcenter.vue';
import neighborhoodMap from './neighborhoodmap/neighborhood-map.vue';
import neighborhoodScores from './neighborhoodscores/neighborhood-scores.vue';
import tabGroup from '@/common/components/tabgroup/tabgroup.presentational.vue';
import exploreHousing from './explore-housing.vue';
import exploreResidents from './explore-residents.vue';
import explorePois from './explore-pois.vue';
import exploreAgents from './explore-agents.vue';
import exploreSchools from './explore-schools.vue';
import mvtNearbyVideo from '@/common/components/mvtnearbyvideo/mvt-nearbyvideo.vue';
import mvtNearbyPlaces from '@/common/components/mvtnearbyplaces/mvt-nearbyplaces.vue';
import SplitTreatments from '@/common/components/split/SplitTreatments.vue';

const NUM_AGENTS = 6;
const NUM_POIS_IN_DESCRIPTION = 5;

export default {
    name: 'NeighborhoodPage',
    inject: ['$eventBus'],
    components: {
        mvtHeader,
        mvtPopSignin,
        mvtFooter,
        mvtDialogCenter,
        mvtToastCenter,
        mvtNearbyVideo,
        mvtNearbyPlaces,
        neighborhoodMap,
        neighborhoodScores,
        tabGroup,
        exploreHousing,
        exploreResidents,
        explorePois,
        exploreAgents,
        exploreSchools,
        SplitTreatments,
    },
    props: {
        pageType: {
            type: String,
            required: true,
        },
        geo: {
            type: Object,
            required: true,
        },
        boundary: {
            type: Object,
            required: true,
        },
        relatedListings: {
            type: Object,
            required: true,
        },
        topNeighborhoodList: {
            type: Array,
            required: false,
        },
        topZipCodeList: {
            type: Array,
            required: false,
        },
        snapshotData: {
            type: Object,
            required: true,
        },
        marketSummary: {
            type: Object,
            required: true,
        },
        weatherSummary: {
            type: Object,
            required: true,
        },
        locationScores: {
            type: Object,
            required: true,
        },
        locationInfo: {
            type: Object,
            required: true,
        },
        pois: {
            type: Array,
            required: true,
        },
        zip: {
            type: String,
            required: true,
        },
        schools: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            agents: null,
            initialTab: 'housing',
        };
    },
    computed: {
        ...mapGetters('neighborhood', ['neighborhood']),
        ...mapGetters('glb', ['glb']),
        description() {
            return `Located in ${this.geo.city}, ${this.geo.state}, ${this.geo.neighborhoodName} currently has ${this.forSaleCount} homes for sale. ${this.descriptorsCopy}Primarily of ${
                this.urbanType
            } makeup, homes in ${this.geo.neighborhoodName} have an average price of ${this.avgPrice}${this.avgSize !== 'NA' ? `, and size of ${this.avgSize} sqft` : ''}.${this.schoolCopy}`;
        },
        poiCopy() {
            const populatedPoiCategories = this?.pois?.filter((category) => !!category?.list?.length && !['LOCAL PASSENGER TRANSPORTATION, NEC', 'BUS STOP'].includes(category.poiTypeName)) ?? [];
            let places = [];
            let i = 0;
            while (places.length < NUM_POIS_IN_DESCRIPTION - 1 && i < NUM_POIS_IN_DESCRIPTION - 1) {
                populatedPoiCategories.forEach((category) => {
                    const item = category?.list?.[i];
                    const containsPlaceByName = places.map((place) => place?.name).includes(item?.name);
                    if (item && places.length < NUM_POIS_IN_DESCRIPTION && !containsPlaceByName) {
                        places.push(item);
                    }
                });
                i++;
            }
            return places.length ? `${places.map((place) => startCase(place?.name.toLowerCase())).join(', ')}, among others. ` : '';
        },
        descriptorsCopy() {
            const descriptors = this.locationInfo?.descriptors;
            if (descriptors?.length) {
                const copy = descriptors.length === 1 ? descriptors[0] : `${descriptors.slice(0, descriptors.length - 1).join(', ')} and ${descriptors.slice(-1)}`;
                return ` Agents tend to describe the neighborhood as ${copy}, with notable places for residents including ${this.poiCopy}`;
            }
            return ` Notable places for residents include ${this.poiCopy}`;
        },
        schoolCopy() {
            const privateSchoolCount = this?.schools?.filter((school) => school?.type === 'private').length ?? 0;
            const publicSchoolCount = this?.schools?.filter((school) => school?.type === 'public').length ?? 0;
            return this.schools?.length
                ? ` There ${this.schools?.length > 1 ? 'are' : 'is'} a total of ${this.schools?.length ?? 0} school${
                      this.schools?.length === 1 ? '' : 's'
                  } in the neighborhood, including ${publicSchoolCount} public, and ${privateSchoolCount} private school${privateSchoolCount === 1 ? '' : 's'}.`
                : '';
        },
        urbanType() {
            const walkScore = this.locationScores?.walk;
            if (this.locationInfo?.urbanType) {
                return this.locationInfo.urbanType.toLowerCase();
            } else if (walkScore > 80) {
                return 'an urban';
            } else if (walkScore < 20) {
                return 'a rural';
            } else {
                return 'a suburban';
            }
        },
        forSaleCount() {
            return this.marketSummary?.homeForSaleCount || 0;
        },
        avgPrice() {
            // return the median price from the snapshot data if it exists (it often does not for some neighborhoods)
            if (this.snapshotData?.today?.allTypeMedianListPriceFormatted) {
                return this.snapshotData.today.allTypeMedianListPriceFormatted;
            } else {
                // return the median price of the zip code in which the majority of listings are located
                const zipCounts = {};
                this.relatedListings?.nearby?.forEach(({ zipCode }) => (zipCounts[zipCode] = (zipCounts?.[zipCode] ?? 0) + 1));
                const mostPrevalentZip = Object.keys(zipCounts)
                    .map((zip) => ({ zip, count: zipCounts[zip] }))
                    .sort((a, b) => (a.count < b.count ? 1 : -1))?.[0]?.zip;
                const matchingZip = this.topZipCodeList?.filter((z) => z.displayName === mostPrevalentZip)?.[0];
                const price = matchingZip ? matchingZip.medianListPriceStr : this.topZipCodeList?.[0]?.medianListPriceStr;
                return price ?? 'NA';
            }
        },
        avgSize() {
            if (this.snapshotData?.today?.allTypeMedianHouseSize) {
                return format.formatDecimalNumberByComma(this.snapshotData.today.allTypeMedianHouseSize);
            } else if (this?.relatedListings?.nearby?.length) {
                return format.formatDecimalNumberByComma(Math.round(this.relatedListings.nearby.map((l) => l.sqftTotal).reduce((acc, curr) => acc + curr, 0) / this.relatedListings.nearby.length));
            } else {
                return 'NA';
            }
        },
        similarNeighborhoods() {
            if (this.locationInfo?.similar?.length) {
                return this.locationInfo.similar.map((hood) => ({
                    ...hood,
                    path: hood?.url ? `/neighborhoods/${hood.url}` : `/neighborhoods/${this.geo.city}-${this.geo.state}/${hood.name.replace(/\s/g, '-')}/`.toLowerCase(),
                }));
            } else if (this.topNeighborhoodList) {
                return this.topNeighborhoodList.map((hood) => ({
                    name: hood.displayName,
                    path: `/neighborhoods/${hood.sitemapNeighborhoodPageUrl}`,
                }));
            }
        },
        tabs() {
            const list = ['housing', 'places', 'school', 'agents'];
            return list.filter((tab) => {
                /* School Tab */
                if (tab === 'school' && !this.categorizedSchoolsList) {
                    return false;
                }
                return true;
            });
        },
        categorizedSchoolsList() {
            const schools = this.neighborhood.nearbySchools;
            const elementarySchools = [];
            const middleSchools = [];
            const highSchools = [];
            schools.forEach(school => {
                if (school.levelCode.includes('E')) {
                    elementarySchools.push(school);
                } else if (school.levelCode.includes('M')) {
                    middleSchools.push(school);
                } else if (school.levelCode.includes('H')) {
                    highSchools.push(school);
                }
            });
            if (elementarySchools.length > 0 && middleSchools.length > 0 && highSchools.length > 0) {
                return [
                    { type: 'Elementary' , list: elementarySchools },
                    { type: 'Middle' , list: middleSchools },
                    { type: 'High' , list: highSchools }
                ]
            } else {
                return null;
            }
        },
    },
    methods: {
        ...mapActions('glb', ['getAgentProfilesByZip']),
        async fetchAgentInfo() {
            this.loading = true;
            const response = await this.getAgentProfilesByZip({ zip: this.zip, limit: NUM_AGENTS });
            this.agents = response?.data;
            this.loading = false;
        },
        openHotlead() {
            const hotleadType = this.glb.isMobile ? 'Neighborhoodpages_Mobile' : 'Neighborhoodpages_Desktop';
            const trigger = this.$options.name;

            this.$eventBus.$emit('dialog-center', {
                name: 'mvtHotleadForm',
                trigger,
                opt: {
                    hotleadSup: 'GET STARTED',
                    hotleadTitle: 'Connect with an agent',
                    buttonText: 'Get Started',
                    zipcode: this.zip,
                    hotleadType,
                },
            });
        },
    },
    mounted() {
        this.fetchAgentInfo();
    },
};
